import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner } from 'react-bootstrap';
import Message from '../../../Components/Message';
import apiClient from '../../../AppClient';
import FertilizationModal from './FertilizationModal';
import { CURRENCY, FERTILIZER_UNIT } from '../../../constants/Constants';

const FertilizationProcess = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [fertilizers, setFertilizers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    loadTransactions();
    fetchFertilizers();
    fetchProducts();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [searchTerm, transactions]);

  const fetchFertilizers = async () => {
    try {
      const response = await apiClient.get('/fertilizers/');
      setFertilizers(response.data);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/products/');
      setProducts(response.data);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    }
  };

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/transactions/fertilizations/');
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : [data]);
      setError(null);
    } catch (error) {
      console.error('خطأ في تحميل المعاملات:', error);
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }

    const filtered = transactions.filter(transaction => {
      const fertilizerName = transaction.fertilizer?.name.toLowerCase() || '';
      const employeeName = transaction.employee_name?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();

      return (
        fertilizerName.includes(searchLower) ||
        employeeName.includes(searchLower)
      );
    });

    setFilteredTransactions(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addFertilization = async (fertilizationData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/transactions/fertilizations/add/', fertilizationData);
      alert('تمت الإضافة بنجاح!');
      loadTransactions();
      setShowAddModal(false);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (formData) => {
    // Process form data from the modal
    const formattedData = {
      fertilizer: parseInt(formData.fertilizer, 10),
      amount: parseFloat(formData.amount),
      products: formData.products.map(id => parseInt(id, 10)),
      employee_name: formData.employee_name,
      notes: formData.notes
    };
  
    addFertilization(formattedData);
  };

  const refreshData = () => {
    loadTransactions();
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  return (
    <Container fluid className="rtl-container" dir="rtl">
      <div className="header-container" style={{ backgroundColor: '#d1e7dd', color: '#0f5132', padding: '1rem', marginBottom: '1rem' }}>
        <h1 className="h3">سجل عمليات التسميد</h1>
      </div>

      {error && <Message variant="danger">{error}</Message>}

      <div className="content-container bg-white rounded shadow p-4 mb-4">
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Control
                type="text"
                placeholder="بحث في المعاملات..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Form.Group>
          </Col>
        <Col md={6} className="d-flex justify-content-md-end">
                    <Button
                      variant="outline-secondary"
                      className="ms-2"
                      onClick={refreshData}
                      style={{marginLeft:'5px'}}
                    >
                         🔄 تحديث
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => setShowAddModal(true)}
                    >
                      <i className="fas fa-plus"></i> إضافة عملية تسميد           
                  </Button>
                  </Col>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#006400' }} />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-5">
            <div className="empty-state">
              <span className="display-4">📄</span>
              <p className="mt-3">لم يتم العثور على معاملات</p>
            </div>
          </div>
        ) : (
          <Table responsive bordered hover>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th>رقم المعاملة</th>
                <th>السماد</th>
                <th>المنتجات</th>
                <th>الكمية</th>
                <th>اسم الموظف</th>
                <th>تاريخ المعاملة</th>
                <th>ملاحظات</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{transaction.id}</td>
                  <td>{transaction.fertilizer?.name || 'غير معروف'}</td>
                  <td>
                    {transaction.products.map(product => product.name).join(', ')}
                  </td>
                  <td>{formatCurrency(transaction.amount)} {FERTILIZER_UNIT}</td>
                  <td>{transaction.employee_name}</td>
                  <td>{transaction.transaction_date}</td>
                  <td>{transaction.notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <FertilizationModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        fertilizers={fertilizers}
        products={products}
        handleSubmit={handleSubmit}
        mode="add"
      />
    </Container>
  );
};

export default FertilizationProcess;
